import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import { useOnMount } from "~/common/hooks/useOnMount";
import { addAllToCart, createWishlistThunk } from "~/Wishlist/redux/actions";
import { selectWishlistThunk, updateComment, wishlistPageMounted } from "~/Wishlist/redux/thunks";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/redux/store";
import Spinner from "~/common/components/Spinner/Spinner";
import {
  getSelectPriceParamsFromWishlistEntry,
  getWishlists,
  selectedSelectedWishlist,
  selectFullWishlist,
  selectIsWishlistsLoading,
  selectSelectedWishlistEntries,
  selectSelectedWishlistId,
} from "~/Wishlist/redux/wishlistSelectors";

import classes from "./WishlistPage.module.less";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { canSeeSalesFinder } from "~/Header/components/headerMenu/DesktopPersonalNav";
import { getProductDescriptorMap } from "~/redux/products/productsSelectors";
import { userIsCustomer, userIsRegistered } from "~/utils/roles";
import { AddToCartButton, RequestOfferButton } from "./components/buttons/WishlistButtons";
import { NoWishlistContent } from "~/Wishlist/page/WishlistPage/components/NoWishlistContent";
import WishlistDropdown from "~/Wishlist/components/WishlistDropdown";
import EditWishlistNamePopup from "~/Wishlist/modal/EditWishlistNamePopup";
import { WishlistDetails } from "~/Wishlist/page/WishlistPage/components/WishlistDetails/WishlistDetails";
import RenameWishlistButton from "./components/buttons/RenameWishlistButton";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { type ExpandedResolvedWishlistEntryModel } from "~/Wishlist/model";
import { DeleteWishlistButton } from "~/Wishlist/page/WishlistPage/components/buttons/DeleteWishlistButton";
import { ShareWishlistButton } from "~/Wishlist/page/WishlistPage/components/buttons/ShareWishlistButton";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/Wishlist/page/WishlistPage/components/buttons/CommentButton";
import { useTranslation } from "react-i18next";
import {
  getTariff,
  resolveSmallestPackageFromProductData,
  selectNetPrice,
} from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { canSeeAddToCartButton } from "~/utils/features";
import { Alert } from "~/common/m3/Alert/Alert";
import { SalesHelper } from "~/SalesCampaign/salesHelper";
import { produce } from "immer";

export const WishlistPage = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  useOnMount(() => dispatch(wishlistPageMounted()));

  const isLoading = useSelector(selectIsWishlistsLoading);
  const wishlists = useSelector(selectWishlists);

  const hasWishlists = wishlists.length > 0;
  const canPurchase = canSeeAddToCartButton();
  const isRegistered = userIsRegistered();
  const isCustomer = userIsCustomer();

  const [createWishlistModal, openCreateWishlistModal] = useCreateWishlistPopup();

  const selectedWishlistId = useSelector(selectSelectedWishlistId);
  const selectedWishlist = useSelector(selectedSelectedWishlist);

  const selectedWishlistIsLoading = !selectedWishlist?.processed;

  const showWSErrorMsg = useSelector(selectShowWSErrorMsg);
  const showATCNotReadyMsg = useSelector(selectShowATCNotReadyMsg);

  const resolvedWishlist = useSelector((state: RootState) => selectFullWishlist(state, selectedWishlistId!));
  const [entries, setEntries] = useState<ExpandedResolvedWishlistEntryModel[]>([]);
  useLayoutEffect(() => {
    const entries = resolvedWishlist?.entries ?? [];
    setEntries((previous) => {
      return entries.map((entry, index) => {
        const packCode = previous[index]?.packCode ?? resolveSmallestPackageFromProductData(entry.product);
        const salesHelper = SalesHelper.of(entry.product);
        const isSaleEnabled = previous[index]?.isSaleEnabled ?? salesHelper.isPackageOnSale(packCode);

        return {
          ...entry,
          isSaleEnabled,
          packCode,
        };
      });
    });
  }, [resolvedWishlist]);

  const makeSetSaleEnabled = (index: number) => (saleEnabled: boolean) => {
    setEntries((previous) => {
      return produce(previous, (draft) => {
        draft[index].isSaleEnabled = saleEnabled;
      });
    });
  };

  const { onSelectWishlist, onSaveComment } = useMemo(() => {
    return {
      onSelectWishlist: (wishlistId: string | undefined) => dispatch(selectWishlistThunk(wishlistId!)),
      onSaveComment: (comment: string) => dispatch(updateComment(comment)),
    };
  }, [dispatch]);

  const onAddSelected = () => {
    const selectedEntries = tableRef.getSelectedRowModel().rows.map((row) => row.original);
    dispatch(addAllToCart(selectedEntries));
  };

  const hasAnySurcharge = useSelector((state: RootState) => {
    for (const entry of entries) {
      const priceParams = getSelectPriceParamsFromWishlistEntry(entry, entry.packCode);
      const netPrice = selectNetPrice(priceParams)(state);
      if (netPrice?.surcharge ?? 0 > 0) {
        return true;
      }
    }
    return false;
  });

  const hasAnyTariffSurcharge = useSelector((state: RootState) => {
    for (const entry of entries) {
      if (getTariff(entry.product)) {
        return true;
      }
    }
    return false;
  });

  const wishlistSelect = (
    <WishlistDropdown
      wishlists={wishlists}
      selectedWishlist={selectedWishlistId}
      onNewWishlist={openCreateWishlistModal}
      onWishlistSelect={onSelectWishlist}
    />
  );

  const tableRef = useReactTable<ExpandedResolvedWishlistEntryModel>({
    data: entries,
    columns: [{ id: "" }],
    getCoreRowModel: getCoreRowModel(),
    getRowId: (data) => data.id,
    getExpandedRowModel: getExpandedRowModel(),
    enableRowSelection: (row) => row.original.isSelectable,
    getRowCanExpand: () => true,
  });

  let ctaButton = null;
  if (!!selectedWishlistId && canPurchase) {
    ctaButton = (
      <AddToCartButton
        className={classes.addToCart}
        onClick={onAddSelected}
        disabled={!tableRef.getIsAllRowsSelected() && !tableRef.getIsSomeRowsSelected()}
      />
    );
  } else if (isRegistered && canSeeSalesFinder()) {
    const selectedEntryIds = tableRef.getSelectedRowModel().rows.map((row) => row.original.id);
    ctaButton = (
      <RequestOfferButton
        className={classes.addToCart}
        wishlistId={selectedWishlistId ?? ""}
        selectedEntryIds={selectedEntryIds}
      />
    );
  }

  const { val: commentVisible, toggle: toggleCommentVisibility, setFalse: hideComment } = useBooleanState(false);
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentVisibility);

  useLayoutEffect(() => {
    hideComment();
  }, [selectedWishlistId]);

  return (
    <>
      {createWishlistModal}
      <PageTitle>{t("wishlistPage.title")}</PageTitle>

      {isLoading && <Spinner />}
      {!isLoading && !hasWishlists && <NoWishlistContent onCreateWishlist={openCreateWishlistModal} />}
      {!isLoading && hasWishlists && selectedWishlist && (
        <div className={classes.content}>
          <div className={classes.wishlistSelect}>{wishlistSelect}</div>

          <div className={classes.actions}>
            <CommentButton onClick={toggleComment} isOpen={commentVisible} hasComment={!!selectedWishlist.comment} />
            <ShareWishlistButton wishlist={selectedWishlist} />
            <RenameWishlistButton wishlist={selectedWishlist} />
            <DeleteWishlistButton wishlist={selectedWishlist} />
            {ctaButton}
          </div>

          {commentVisible && (
            <div className={classes.commentBlock}>
              <div className={classes.commentBlockTitle}>{t("wishlistPage.comment.title")}</div>
              <CommentInput
                value={selectedWishlist.comment}
                onChange={onSaveComment}
                ref={commentRef}
                label={t("wishlistPage.comment.label")}
              />
            </div>
          )}

          <div className={classes.messages}>
            {showATCNotReadyMsg && <Alert severity={"info"}>{t("wishlistPage.fetchingInProgress")}</Alert>}
            {showWSErrorMsg && <Alert severity={"info"}>{t("wishlistPage.wsError")}</Alert>}
          </div>

          {selectedWishlistIsLoading && <Spinner />}
          {!selectedWishlistIsLoading && selectedWishlist && (
            <>
              <WishlistDetails
                isCustomer={isCustomer}
                tableRef={tableRef}
                className={classes.details}
                makeSetSaleEnabled={makeSetSaleEnabled}
              />
            </>
          )}
          {
            <IncludesSurchargeDisclaimer
              hasSurcharge={hasAnySurcharge}
              tariffSurcharge={hasAnyTariffSurcharge}
              className={classes.surcharge}
            />
          }
        </div>
      )}
    </>
  );
};

const useCreateWishlistPopup = () => {
  const { val: isModalOpen, setTrue, setFalse } = useBooleanState(false);

  const dispatch = useDispatch();
  const createWishlistCb = useCallback(
    async (name: string) => {
      setFalse();
      const wishlistsData = await dispatch(createWishlistThunk(name));
      const wishlist = wishlistsData.wishlists[0];
      if (wishlist) {
        dispatch(selectWishlistThunk(wishlist.id));
      }
    },
    [setFalse],
  );

  const modal = isModalOpen && <EditWishlistNamePopup onCancel={setFalse} onOk={createWishlistCb} />;
  return [modal, setTrue] as const;
};

const selectWishlists = (state: RootState) => getWishlists(state.wishlistPage);

const selectShowATCNotReadyMsg = (state: RootState) => {
  const wishlistEntries = selectSelectedWishlistEntries(state);
  const productsMap = getProductDescriptorMap(state, { wishlistEntries });

  return state.wishlistPage.addAllClicked && wishlistEntries.some((entry) => productsMap[entry.productCode].isLoading);
};

const selectShowWSErrorMsg = (state: RootState) => {
  const wishlistEntries = selectSelectedWishlistEntries(state);
  const productsMap = getProductDescriptorMap(state, { wishlistEntries });

  return state.wishlistPage.addAllClicked && wishlistEntries.some((entry) => productsMap[entry.productCode].isError);
};

export function createWishlistPage() {
  return <WishlistPage />;
}
